import { useState, useEffect, useCallback, useId } from 'react';
import { FaCheck, FaArrowLeft, FaExclamationTriangle, FaArchive, FaUserFriends, FaTrash } from 'react-icons/fa';
import { Steps } from '@chakra-ui/react';
import { useUser } from '../../contexts/UserContext';
import { useToast } from '../../contexts/ToastContext';
import { checkExperiencePlans, deleteExperience, transferOwnership, archiveExperience } from '../../utilities/experiences-api';
import { searchUsers } from '../../utilities/search-api';
import { handleError } from '../../utilities/error-handler';
import { lang } from '../../lang.constants';
import { logger } from '../../utilities/logger';
import { Button, Text, Modal } from '../design-system';
import Autocomplete from '../Autocomplete/Autocomplete';
import Banner from '../Banner/Banner';
import Loading from '../Loading/Loading';
import styles from './TransferOwnershipModal.module.css';

// Get transfer ownership strings from lang constants
const t = () => lang.current.modal?.transferOwnership || {};

/**
 * Steps in the transfer ownership flow
 */
const STEPS = {
  LOADING: 0,
  CHOOSE_ACTION: 1,
  SELECT_USER: 2,
  CONFIRM: 3,
};

/**
 * Actions available when deleting an experience with plans
 */
const ACTIONS = {
  TRANSFER: 'transfer',
  ARCHIVE: 'archive',
  DELETE: 'delete', // Only available when no plans exist
};

/**
 * TransferOwnershipModal - Multi-step modal for handling experience deletion
 * when other users have created plans for the experience.
 *
 * Migrated to Chakra UI via design-system abstraction with enhanced
 * accessibility: useId for unique IDs, aria-live for status messages,
 * ARIA step indicator, keyboard-accessible action cards, and semantic roles.
 *
 * Flow:
 * 1. Check for existing plans
 * 2. If plans exist:
 *    a. Choose action (transfer to user OR archive)
 *    b. If transfer: select user from collaborators or search
 *    c. Confirm action
 * 3. If no plans: show standard delete confirmation
 *
 * Task: biensperience-26f9
 */
export default function TransferOwnershipModal({
  show,
  onClose,
  experience,
  onSuccess,
}) {
  const { user } = useUser();
  const { success: showSuccess, error: showError } = useToast();
  const id = useId();

  // Step management
  const [currentStep, setCurrentStep] = useState(STEPS.LOADING);
  const [selectedAction, setSelectedAction] = useState(null);

  // Plan check data
  const [planCheckData, setPlanCheckData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // User selection
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Check for plans - wrapped in useCallback for dependency tracking
  const checkPlans = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Validate experience ID before making the request
      const experienceId = experience?._id;
      if (!experienceId) {
        throw new Error(t().errorMissingExperienceId || 'Experience ID is missing');
      }
      const result = await checkExperiencePlans(experienceId);
      setPlanCheckData(result);

      // If no plans exist, skip to delete confirmation
      if (!result.requiresTransfer && result.canDelete) {
        setSelectedAction(ACTIONS.DELETE);
        setCurrentStep(STEPS.CONFIRM);
      } else {
        setCurrentStep(STEPS.CHOOSE_ACTION);
      }
    } catch (err) {
      const errorMsg = handleError(err, { context: 'Check experience plans', silent: true });
      setError(errorMsg || t().errorCheckFailed || 'Failed to check experience status');
      setCurrentStep(STEPS.CHOOSE_ACTION);
    } finally {
      setLoading(false);
    }
  }, [experience?._id]);

  // Check for plans when modal opens
  useEffect(() => {
    if (show && experience?._id) {
      checkPlans();
    }
  }, [show, experience?._id, checkPlans]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setCurrentStep(STEPS.LOADING);
      setSelectedAction(null);
      setPlanCheckData(null);
      setError('');
      setSelectedUser(null);
      setUserSearchTerm('');
      setSearchResults([]);
    }
  }, [show]);

  // Search for users to transfer to
  const handleUserSearch = useCallback(async (query) => {
    setUserSearchTerm(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await searchUsers(query);
      // Filter out current user and format for autocomplete
      const filtered = (results || [])
        .filter(u => u._id !== user?._id)
        .map(u => ({
          id: u._id,
          _id: u._id,
          name: u.name,
          email: u.email,
        }));
      setSearchResults(filtered);
    } catch (err) {
      logger.error('User search failed:', { error: err.message }, err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [user?._id]);


  // Handle action selection
  const handleActionSelect = (action) => {
    setSelectedAction(action);
    if (action === ACTIONS.TRANSFER) {
      setCurrentStep(STEPS.SELECT_USER);
    } else {
      // Archive goes straight to confirm
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  // Handle user selection
  const handleUserSelect = (selectedUserData) => {
    setSelectedUser(selectedUserData);
    setCurrentStep(STEPS.CONFIRM);
  };

  // Go back to previous step
  const handleBack = () => {
    if (currentStep === STEPS.CONFIRM) {
      if (selectedAction === ACTIONS.TRANSFER) {
        setCurrentStep(STEPS.SELECT_USER);
      } else {
        setCurrentStep(STEPS.CHOOSE_ACTION);
      }
    } else if (currentStep === STEPS.SELECT_USER) {
      setCurrentStep(STEPS.CHOOSE_ACTION);
    }
    setError('');
  };

  // Execute the selected action
  const handleConfirm = async () => {
    setLoading(true);
    setError('');

    try {
      if (selectedAction === ACTIONS.DELETE) {
        await deleteExperience(experience._id);
        showSuccess(t().successDeleted?.replace('{name}', experience.name) || `"${experience.name}" has been deleted.`);
      } else if (selectedAction === ACTIONS.TRANSFER) {
        if (!selectedUser?._id) {
          setError(t().errorMissingUser || 'Please select a user to transfer ownership to.');
          setLoading(false);
          return;
        }
        await transferOwnership(experience._id, selectedUser._id);
        showSuccess(
          t().successTransferred?.replace('{name}', experience.name).replace('{newOwner}', selectedUser.name) ||
          `Ownership of "${experience.name}" has been transferred to ${selectedUser.name}.`
        );
      } else if (selectedAction === ACTIONS.ARCHIVE) {
        await archiveExperience(experience._id);
        showSuccess(t().successArchived?.replace('{name}', experience.name) || `"${experience.name}" has been archived.`);
      }

      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      const errorMsg = handleError(err, { context: 'Process experience', silent: true });
      setError(errorMsg || t().errorProcessFailed || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  // Get modal title based on current step
  const getModalTitle = () => {
    if (currentStep === STEPS.LOADING) return t().titleChecking || 'Checking...';
    if (currentStep === STEPS.CHOOSE_ACTION) return t().titleDeleteExperience || 'Delete Experience';
    if (currentStep === STEPS.SELECT_USER) return t().titleTransferOwnership || 'Transfer Ownership';
    if (currentStep === STEPS.CONFIRM) return t().titleConfirmAction || 'Confirm Action';
    return t().titleDeleteExperience || 'Delete Experience';
  };

  // Render step content
  const renderStepContent = () => {
    if (currentStep === STEPS.LOADING) {
      return (
        <div className={styles.loadingState}>
          <Loading size="md" />
          <Text variant="muted" className={styles.mt3}>{t().checkingStatus || 'Checking experience status...'}</Text>
        </div>
      );
    }

    if (currentStep === STEPS.CHOOSE_ACTION) {
      const hasPlans = planCheckData?.totalPlans > 0;
      const count = planCheckData?.otherUserPlansCount || 0;

      return (
        <div className={styles.stepContent}>
          {hasPlans ? (
            <>
              <div className={styles.warningBanner} role="alert">
                <FaExclamationTriangle className={styles.warningIcon} aria-hidden="true" />
                <div>
                  <Text weight="semibold">
                    {count === 1
                      ? (t().hasActivePlanSingular || 'This experience has an active plan')
                      : (t().hasActivePlansPlural || 'This experience has active plans')}
                  </Text>
                  <Text size="sm" variant="muted">
                    {count === 1
                      ? (t().otherUserHasPlanSingular?.replace('{count}', count) || `${count} other user has created a plan for this experience.`)
                      : (t().otherUsersHavePlansPlural?.replace('{count}', count) || `${count} other users have created plans for this experience.`)}
                  </Text>
                </div>
              </div>

              <Text className={styles.mb4}>
                {t().cannotDeleteDirectly || "Since other users are planning this experience, you cannot delete it directly. Choose how you'd like to proceed:"}
              </Text>

              <div className={styles.actionCards} role="group" aria-label="Available actions">
                <button
                  type="button"
                  className={styles.actionCard}
                  onClick={() => handleActionSelect(ACTIONS.TRANSFER)}
                  aria-describedby={`${id}-transfer-desc`}
                >
                  <div className={styles.actionIcon}>
                    <FaUserFriends aria-hidden="true" />
                  </div>
                  <div className={styles.actionContent}>
                    <Text weight="semibold">{t().transferOwnershipAction || 'Transfer Ownership'}</Text>
                    <Text size="sm" variant="muted" id={`${id}-transfer-desc`}>
                      {t().transferOwnershipDescription || 'Give ownership to another user who can continue managing the experience.'}
                    </Text>
                  </div>
                </button>

                <button
                  type="button"
                  className={styles.actionCard}
                  onClick={() => handleActionSelect(ACTIONS.ARCHIVE)}
                  aria-describedby={`${id}-archive-desc`}
                >
                  <div className={styles.actionIcon}>
                    <FaArchive aria-hidden="true" />
                  </div>
                  <div className={styles.actionContent}>
                    <Text weight="semibold">{t().archiveExperienceAction || 'Archive Experience'}</Text>
                    <Text size="sm" variant="muted" id={`${id}-archive-desc`}>
                      {t().archiveExperienceDescription || 'Move to archive. The experience remains accessible to users with plans, but will no longer appear in public listings.'}
                    </Text>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <div className={styles.deleteConfirm}>
              <FaTrash className={styles.deleteIcon} aria-hidden="true" />
              <Text weight="semibold" className={styles.mb2}>{t().titleDeleteExperience || 'Delete Experience'}?</Text>
              <Text variant="muted">
                {t().noPlansExist || 'No users have created plans for this experience. You can safely delete it.'}
              </Text>
            </div>
          )}
        </div>
      );
    }

    if (currentStep === STEPS.SELECT_USER) {
      return (
        <div className={styles.stepContent}>
          <Text className={styles.mb4}>
            {t().searchUserPrompt?.replace('{name}', experience?.name) || `Search for a user to transfer ownership of "${experience?.name}" to:`}
          </Text>

          <div className={styles.searchSection} role="search" aria-label={t().searchPlaceholder || 'Search by name or email'}>
            <Autocomplete
              placeholder={t().searchPlaceholder || 'Search by name or email...'}
              value={userSearchTerm}
              items={searchResults}
              onSelect={handleUserSelect}
              onSearch={handleUserSearch}
              size="md"
              emptyMessage={searching ? (t().searchingUsers || 'Searching...') : (t().typeToSearch || 'Type to search users...')}
              entityType="user"
              disableFilter={true}
            />
          </div>

          {selectedUser && (
            <div className={styles.selectedUserPreview}>
              <Text size="sm" weight="semibold" className={styles.mb2}>
                {t().selectedUser || 'Selected user:'}
              </Text>
              <div className={styles.userCard}>
                <div className={styles.userInfo}>
                  <Text weight="medium">{selectedUser.name}</Text>
                  <Text size="sm" variant="muted">{selectedUser.email}</Text>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (currentStep === STEPS.CONFIRM) {
      return (
        <div className={styles.stepContent}>
          <div className={styles.confirmContent}>
            {selectedAction === ACTIONS.DELETE && (
              <>
                <FaTrash className={styles.confirmIcon} style={{ color: 'var(--color-danger)' }} aria-hidden="true" />
                <Text weight="semibold" size="lg" className={styles.mb2}>
                  {t().confirmDelete?.replace('{name}', experience?.name) || `Delete "${experience?.name}"?`}
                </Text>
                <Text variant="muted">
                  {t().confirmDeleteWarning || 'This action cannot be undone. The experience and all its data will be permanently removed.'}
                </Text>
              </>
            )}

            {selectedAction === ACTIONS.TRANSFER && selectedUser && (
              <>
                <FaUserFriends className={styles.confirmIcon} style={{ color: 'var(--color-primary)' }} aria-hidden="true" />
                <Text weight="semibold" size="lg" className={styles.mb2}>
                  {t().confirmTransfer?.replace('{name}', selectedUser.name) || `Transfer to ${selectedUser.name}?`}
                </Text>
                <Text variant="muted">
                  {t().confirmTransferWarning?.replace('{name}', selectedUser.name).replace('{experienceName}', experience?.name) ||
                    `${selectedUser.name} will become the new owner of "${experience?.name}" and will have full control over it. You will lose ownership access.`}
                </Text>
              </>
            )}

            {selectedAction === ACTIONS.ARCHIVE && (
              <>
                <FaArchive className={styles.confirmIcon} style={{ color: 'var(--color-warning)' }} aria-hidden="true" />
                <Text weight="semibold" size="lg" className={styles.mb2}>
                  {t().confirmArchive?.replace('{name}', experience?.name) || `Archive "${experience?.name}"?`}
                </Text>
                <Text variant="muted">
                  {t().confirmArchiveWarning || "The experience will be moved to archive. Users with existing plans can still access it, but it won't appear in public listings or search results."}
                </Text>
              </>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Get button text based on action
  const getConfirmButtonText = () => {
    if (loading) return t().buttonProcessing || 'Processing...';
    if (selectedAction === ACTIONS.DELETE) return t().buttonDeletePermanently || 'Delete Permanently';
    if (selectedAction === ACTIONS.TRANSFER) return t().buttonTransferOwnership || 'Transfer Ownership';
    if (selectedAction === ACTIONS.ARCHIVE) return t().buttonArchiveExperience || 'Archive Experience';
    return lang.current.button?.confirm || 'Confirm';
  };

  // Get button variant based on action
  const getConfirmButtonVariant = () => {
    if (selectedAction === ACTIONS.DELETE) return 'danger';
    return 'gradient';
  };

  // Render custom footer
  const renderFooter = () => (
    <div className={styles.modalFooter}>
      <div className={styles.footerLeft}>
        {(currentStep === STEPS.SELECT_USER || currentStep === STEPS.CONFIRM) && (
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBack}
            disabled={loading}
            aria-label={t().buttonBackToPrevious || 'Go back to previous step'}
          >
            <FaArrowLeft size={12} className={styles.me2} aria-hidden="true" />
            {t().buttonBack || 'Back'}
          </button>
        )}
      </div>
      <div className={styles.footerRight}>
        <button
          type="button"
          className={`btn btn-outline-secondary ${styles.cancelButton}`}
          onClick={onClose}
          disabled={loading}
        >
          {t().buttonCancel || lang.current.button?.cancel || 'Cancel'}
        </button>
        {currentStep === STEPS.CONFIRM && (
          <Button
            variant={getConfirmButtonVariant()}
            size="md"
            onClick={handleConfirm}
            disabled={loading || (selectedAction === ACTIONS.TRANSFER && !selectedUser)}
            aria-busy={loading}
          >
            {getConfirmButtonText()}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={getModalTitle()}
      size="lg"
      scrollable
      centered
      footer={renderFooter()}
      contentClassName={styles.modalContentWrapper}
      loading={loading}
      showSubmitButton={false}
    >
      {/* Step Indicator */}
      {currentStep !== STEPS.LOADING && planCheckData?.requiresTransfer && (() => {
        // Build dynamic step list — "Select User" only appears for transfer
        const isTransfer = selectedAction === ACTIONS.TRANSFER;
        const dynamicSteps = [
          { key: 'action', title: 'Choose Action', stepValue: STEPS.CHOOSE_ACTION },
          ...(isTransfer ? [{ key: 'user', title: 'Select User', stepValue: STEPS.SELECT_USER }] : []),
          { key: 'confirm', title: 'Confirm', stepValue: STEPS.CONFIRM },
        ];
        // Map currentStep (which uses the STEPS enum values 1/2/3) to
        // a 0-based index in the dynamic array for the Chakra indicator.
        const activeIndex = dynamicSteps.findIndex(s => s.stepValue === currentStep);
        const resolvedIndex = activeIndex >= 0 ? activeIndex : 0;

        return (
          <Steps.Root step={resolvedIndex} count={dynamicSteps.length} size="sm" colorPalette="blue" mb="3">
            <Steps.List>
              {dynamicSteps.map((s, i) => (
                <Steps.Item key={s.key} index={i}>
                  <Steps.Indicator>
                    <Steps.Status
                      complete={<FaCheck size={10} />}
                      incomplete={<Steps.Number />}
                      current={<Steps.Number />}
                    />
                  </Steps.Indicator>
                  <Steps.Title>{s.title}</Steps.Title>
                  <Steps.Separator />
                </Steps.Item>
              ))}
            </Steps.List>
          </Steps.Root>
        );
      })()}

      {/* Error Banner */}
      <div aria-live="assertive" aria-atomic="true">
        {error && (
          <Banner
            type="danger"
            variant="light"
            message={error}
            dismissible
            onDismiss={() => setError('')}
            className={styles.mb4}
          />
        )}
      </div>

      {/* Step Content */}
      {renderStepContent()}
    </Modal>
  );
}
